/**
 * Checkout Form component
 * @returns <CheckoutForm />
 */
import { useState } from "react";

import AccordionItem from "./AccordionItem";
import InputCheckBox from "./InputCheckBox";
import InputText from "./InputText";
import Button from "./Button";
import ButtonCard from "./ButtonCard";
import { useNavigate } from "react-router-dom";

import {
    useGetAllBranchesQuery,
    useCheckoutCartMutation,
} from "../redux/apiSlice";
import { maskRut, maskPhoneNumber } from "../scripts/helpers/masks";

import {
    validateHasValue,
    validatePhoneNumber,
    validateEmail,
    validateRut,
    vinIsValid,
} from "../scripts/helpers/validateFunctions";

const CheckoutForm = (props) => {
    const { totalPrice } = props;
    const navigate = useNavigate();

    const {
        data: dealerships,
        isFetching,
        isLoading,
        isSuccess,
        isError,
    } = useGetAllBranchesQuery();

    const [checkoutCart] = useCheckoutCartMutation();

    const [name, setName] = useState({ value: "", isValid: null, error: null });
    const [phone, setPhone] = useState({
        value: "+56",
        isValid: null,
        error: null,
    });
    const [email, setEmail] = useState({
        value: "",
        isValid: null,
        error: null,
    });
    const [rut, setRut] = useState({ value: "", isValid: null, error: null });
    const [vin, setVin] = useState({ value: "", isValid: null, error: null });
    const [dealership, setDealership] = useState({
        value: "",
        text: "",
        isValid: null,
        error: null,
    });
    const [dealershipDetails, setDealershipDetails] = useState(false);
    const [acceptTermsAndConditions, setAcceptTermsAndConditions] =
        useState(false);
    const [receiveCommercialInformation, setReceiveCommercialInformation] =
        useState(false);

    const [personalDataOpen, setPersonalDataOpen] = useState(true);
    const [vinOpen, setVinOpen] = useState(true);
    const [dealershipOpen, setDealershipOpen] = useState(true);
    const [dealershipDetailsOpen, setDealershipDetailsOpen] = useState(true);

    const validatePersonalData = () => {
        // console.log("validatePersonalData runs");

        const [isNameValid, nameError] = validateHasValue(name);
        const [isPhoneValid, phoneError] = validatePhoneNumber(phone);
        const [isEmailValid, emailError] = validateEmail(email);
        const [isRutValid, rutError] = validateRut(rut);

        setName((prevState) => {
            return {
                ...prevState,
                isValid: isNameValid,
                error: nameError,
            };
        });

        setPhone((prevState) => {
            return {
                ...prevState,
                isValid: isPhoneValid,
                error: phoneError,
            };
        });

        setEmail((prevState) => {
            return {
                ...prevState,
                isValid: isEmailValid,
                error: emailError,
            };
        });

        setRut((prevState) => {
            return {
                ...prevState,
                isValid: isRutValid,
                error: rutError,
            };
        });

        if (isNameValid && isPhoneValid && isEmailValid && isRutValid) {
            setPersonalDataOpen(!personalDataOpen);
        }
    };

    const validateVin = () => {
        if (props.isVinOptional) {
            setVin((prevState) => {
                return {
                    ...prevState,
                    isValid: true,
                    error: "",
                };
            });

            setVinOpen(!vinOpen);
        } else {
            // console.log("validateVin runs");

            const [isVinValid, vinError] = vinIsValid(vin);

            setVin((prevState) => {
                return {
                    ...prevState,
                    isValid: isVinValid,
                    error: vinError,
                };
            });

            if (isVinValid) {
                setVinOpen(!vinOpen);
            }
        }
    };

    const isDealershipSelected = () => {
        let isValid;
        let error;

        if (dealership.value !== null && dealership.value !== "") {
            // console.log("is valid");
            isValid = true;
            error = null;
        } else {
            // console.log("is invalid");
            isValid = false;
            error = "Debés seleccionar un concesionario";
        }

        return [isValid, error];
    };

    const validateDealership = () => {
        const [isDealerSelected, dealershipError] = isDealershipSelected();

        setDealership((prevState) => {
            return {
                ...prevState,
                isValid: isDealerSelected,
                error: dealershipError,
            };
        });

        if (isDealerSelected) {
            setDealershipOpen(!dealershipOpen);
        }
    };

    const validateDealershipDetails = () => {
        const [isDealerSelected, dealershipError] = isDealershipSelected();

        if (isDealerSelected) {
            setDealershipDetails(true);
            setDealershipDetailsOpen(!dealershipDetailsOpen);
        }
    };

    // const dealershipClickHandler = (value) => {
    //     setDealership((prevState) => {
    //         return {
    //             ...prevState,
    //             value: value
    //         }
    //     })
    // }

    const dealershipClickHandler = (index) => {
        setDealershipDetails(false);
        // setDealershipDetailsOpen(true)

        setDealership((prevState) => {
            return {
                ...prevState,
                value: index !== prevState.value ? index : null,
                isValid: index !== prevState.value ? prevState.isValid : false,
            };
        });
    };

    const personalDataValidity =
        name.isValid && phone.isValid && email.isValid && rut.isValid;
    const vinValidity = vin.isValid;
    const dealershipValidity = dealership.isValid;
    // const dealershipDetailsValidity = dealershipDetails;

    const webPayDataObject = {
        full_name: name.value,
        rut: rut.value,
        email: email.value,
        phone_number: phone.value,
        branch: dealership.value,
        salesman: dealership.text,
        vehicle_id: vin.value,
    };

    const webpayHandler = () => {
        checkoutCart(webPayDataObject)
            .then((res) => {
                window.location.href = res.data.redirect_url;
                // navigate(res.data.redirect_url)
            })
            .catch(() => {
                navigate("/error");
            });
    };

    return (
        <div className="book-form">
            <AccordionItem
                type="checkout"
                item={{}}
                accordionItemLabel={"Datos personales"}
                isOpen={personalDataOpen}
                clicked={validatePersonalData}
                isValid={personalDataValidity}
                toggle={() => setPersonalDataOpen(!personalDataOpen)}
            >
                <div className="checkout__input-list">
                    <InputText
                        type={"text"}
                        id={"nombreApellido"}
                        name={"nombreApellido"}
                        label={"Nombre y apellido"}
                        placeholder={"Ingresa tu nombre y apellido"}
                        isRequired={true}
                        changed={(e) => {
                            setName((prevState) => {
                                return {
                                    ...prevState,
                                    value: e.target.value,
                                };
                            });
                        }}
                        disabled={false}
                        error={name.error}
                        value={name.value}
                    />
                    <InputText
                        type={"text"}
                        id={"RUT"}
                        name={"RUT"}
                        label={"RUT"}
                        placeholder={"Ingresa tu RUT"}
                        isRequired={true}
                        disabled={false}
                        changed={(e) => {
                            setRut((prevState) => {
                                return {
                                    ...prevState,
                                    value: maskRut(e.target.value),
                                };
                            });
                        }}
                        error={rut.error}
                        value={rut.value}
                    />

                    <div className="checkout__inputs-group">
                        <InputText
                            type={"text"}
                            id={"correoElectronico"}
                            name={"correoElectronico"}
                            label={"Correo electrónico"}
                            placeholder={"Ingresa tu correo electrónico"}
                            isRequired={true}
                            disabled={false}
                            changed={(e) => {
                                setEmail((prevState) => {
                                    return {
                                        ...prevState,
                                        value: e.target.value,
                                    };
                                });
                            }}
                            // isValid={formState.nombreApellido.isValid}
                            error={email.error}
                            value={email.value}
                        />
                        <InputText
                            type={"text"}
                            id={"telefono"}
                            name={"telefono"}
                            label={"Teléfono"}
                            placeholder={"Ingresa tu teléfono"}
                            isRequired={true}
                            disabled={false}
                            changed={(e) => {
                                setPhone((prevState) => {
                                    return {
                                        ...prevState,
                                        // value: maskPhoneNumber(e.target.value)
                                        value: e.target.value,
                                    };
                                });
                            }}
                            // isValid={formState.nombreApellido.isValid}
                            error={phone.error}
                            value={phone.value}
                        />
                    </div>
                </div>
            </AccordionItem>

            <AccordionItem
                type="checkout"
                item={{}}
                accordionItemLabel={"Identifica tu vehículo"}
                isOpen={vinOpen}
                clicked={() => validateVin()}
                isValid={vinValidity}
                toggle={() => setVinOpen(!vinOpen)}
                previousSectionValid={personalDataValidity}
            >
                <InputText
                    type={"text"}
                    id={"patente"}
                    name={"patente"}
                    label={"Patente o VIN"}
                    placeholder={
                        "Ingresa tu patente o VIN (si tu auto es nuevo)"
                    }
                    isRequired={true}
                    changed={(e) => {
                        setVin((prevState) => {
                            return {
                                ...prevState,
                                value: e.target.value,
                            };
                        });
                    }}
                    disabled={!personalDataValidity}
                    error={vin.error}
                    value={vin.value}
                />
            </AccordionItem>

            <AccordionItem
                type="checkout"
                item={{}}
                accordionItemLabel={"Concesionario"}
                isOpen={dealershipOpen}
                clicked={() => validateDealership()}
                isValid={dealershipValidity}
                toggle={() => setDealershipOpen(!dealershipOpen)}
                previousSectionValid={vinValidity}
            >
                <InputText
                    type={"text"}
                    id={"concesionario"}
                    name={"concesionario"}
                    label={"Código de vender o asesor de servicio"}
                    placeholder={
                        "Solo si compraste orientado por un asesor de servicio *"
                    }
                    isRequired={false}
                    changed={(e) => {
                        setDealership((prevState) => {
                            return {
                                ...prevState,
                                text: e.target.value,
                            };
                        });
                    }}
                    // isValid={formState.nombreApellido.isValid}
                    // error={dealership.error}
                    value={dealership.text}
                    disabled={!vinValidity}
                />

                <p className="fw-300 dealership-title">
                    Selecciona el concesionario donde desees realizar la mantención y/o instalación y/o compra accesorio
                </p>

                <div className="btn-card__container">
                    {dealerships?.map((dealer, i) => {
                        return (
                            <ButtonCard
                                card={dealer}
                                key={i}
                                clicked={() =>
                                    dealershipClickHandler(dealer.id)
                                }
                                isSelected={dealer.id === dealership.value}
                                disabled={!vinValidity}
                            />
                        );
                    })}
                </div>
            </AccordionItem>

            {/* <AccordionItem item={{}} type="checkout" accordionItemLabel={"Detalles de envío"} isOpen={dealershipDetailsOpen} clicked={() => validateDealershipDetails()} isValid={dealershipDetailsValidity} toggle={() => setDealershipDetailsOpen(!dealershipDetailsOpen)} previousSectionValid={dealershipValidity}>
                {dealership.isValid
                    ?
                    <>
                        <p className="dealership-text date">Podes retirarlo a partir del <strong>{dealerships[dealership.value].pickupDate}</strong> en:</p>
                        <p className="dealership-text dealership"><strong>{dealerships[dealership.value].name}</strong></p>
                        <p className="dealership-text address">{dealerships[dealership.value].address} | {dealerships[dealership.value].phone}</p>
                        <p className="dealership-text week">{dealerships[dealership.value].openDays.week}</p>
                        <p className="dealership-text weekend">{dealerships[dealership.value].openDays.weekend}</p>
                    </>
                    :
                    <p>Por favor selecciona un consecionario</p>
                }
            </AccordionItem> */}

            <InputCheckBox
                type={"checkbox"}
                id={"terminosCondiciones"}
                name={"terminosCondiciones"}
                label={"He leído y acepto todos los Términos y Condiciones"}
                value={"acepto-terminos-y-condiciones"}
                isRequired={true}
                changed={() =>
                    setAcceptTermsAndConditions(!acceptTermsAndConditions)
                }
                isChecked={acceptTermsAndConditions}
                extraClass={"checkout__radio"}
                // disabled={accordionsValid ? false : true}
                disabled={!dealershipValidity}
            />
            {/* <InputCheckBox
                type={"checkbox"}
                id={"informacionComercial"}
                name={"informacionComercial"}
                label={"Acepto recibir información comercial."}
                value={"acepto-recibir-informacion-comercial"}
                isRequired={false}
                changed={() => setReceiveCommercialInformation(!receiveCommercialInformation)}
                isChecked={receiveCommercialInformation}
                extraClass={"checkout__radio"}
                // disabled={false}
                disabled={!dealershipValidity}
            /> */}

            <div
                className={`checkout__webpay ${acceptTermsAndConditions ? "--active" : ""
                    }`}
            >
                <p className="purchase-total">
                    El valor de la compra es de ${totalPrice}
                </p>

                <div className="webpay">
                    <p className="fw-300">Pago online vía</p>
                    <img src={`${window._env_.STATIC_HOST}/static/website/images/icon-webpay.svg`} alt="" />
                </div>

                <Button
                    type={"submit"}
                    tag={"cart-with-total-1-line"}
                    text={"Pagar ahora"}
                    hasIcon={true}
                    totalPrice={totalPrice}
                    // disabled={!checkboxChecked["terminosCondiciones"]}
                    disabled={!acceptTermsAndConditions}
                    onClick={() => webpayHandler()}
                />
            </div>
        </div>
    );
};

export default CheckoutForm;
